
import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function PackageInsertSearch() {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('name');

  const quickLinks = [
    {
      title: 'PMDA添付文書検索',
      description: '医薬品医療機器総合機構の公式添付文書データベース',
      url: 'https://www.pmda.go.jp/PmdaSearch/iyakuSearch/',
      icon: 'ri-government-line',
      color: 'bg-blue-50 text-blue-600'
    },
    {
      title: '医薬品情報提供ホームページ',
      description: 'PMDAの医薬品・医療機器等安全性情報',
      url: 'https://www.pmda.go.jp/safety/info-services/drugs/0001.html',
      icon: 'ri-shield-check-line',
      color: 'bg-green-50 text-green-600'
    },
    {
      title: 'おくすり110番',
      description: '薬の検索と基本情報',
      url: 'https://www.jah.ne.jp/~kako/frame_dwm_search.html',
      icon: 'ri-search-line',
      color: 'bg-purple-50 text-purple-600'
    },
    {
      title: 'インタビューフォーム検索',
      description: '日本病院薬剤師会のIF検索システム',
      url: 'https://www.jshp.or.jp/',
      icon: 'ri-file-text-line',
      color: 'bg-orange-50 text-orange-600'
    }
  ];

  const searchCategories = [
    {
      id: 'name',
      label: '薬品名',
      placeholder: '薬品名を入力してください（例：アムロジピン）'
    },
    {
      id: 'ingredient',
      label: '成分名',
      placeholder: '成分名を入力してください（例：amlodipine）'
    },
    {
      id: 'code',
      label: '薬価基準収載医薬品コード',
      placeholder: 'コードを入力してください（例：2171022F1）'
    },
    {
      id: 'company',
      label: '製薬会社名',
      placeholder: '会社名を入力してください（例：ファイザー）'
    }
  ];

  const recentUpdates = [
    {
      date: '2024.12.20',
      title: '新型コロナウイルス治療薬の添付文書改訂',
      category: '安全性情報'
    },
    {
      date: '2024.12.18',
      title: '抗凝固薬の相互作用に関する注意喚起',
      category: '重要な安全性情報'
    },
    {
      date: '2024.12.15',
      title: '糖尿病治療薬の用法・用量変更',
      category: '添付文書改訂'
    },
    {
      date: '2024.12.12',
      title: '抗がん剤の副作用情報更新',
      category: '安全性情報'
    }
  ];

  const usefulFeatures = [
    {
      title: '高度検索機能',
      description: '複数条件での絞り込み検索',
      icon: 'ri-filter-line',
      features: ['薬効分類での検索', '承認年月日での絞り込み', '剤形での分類', '適応症での検索']
    },
    {
      title: 'PDF一括ダウンロード',
      description: '添付文書のPDF形式での保存',
      icon: 'ri-download-line',
      features: ['最新版の自動取得', '履歴管理', 'オフライン閲覧', '印刷最適化']
    },
    {
      title: '変更履歴追跡',
      description: '添付文書の改訂履歴確認',
      icon: 'ri-history-line',
      features: ['改訂日時の確認', '変更箇所のハイライト', '旧版との比較', '改訂理由の表示']
    },
    {
      title: 'お気に入り機能',
      description: 'よく使用する薬剤の登録',
      icon: 'ri-bookmark-line',
      features: ['薬剤リストの作成', 'カテゴリ分類', '更新通知', 'チーム共有']
    }
  ];

  const handleSearch = () => {
    if (searchTerm.trim()) {
      const searchUrl = `https://www.pmda.go.jp/PmdaSearch/iyakuSearch/`;
      window.open(searchUrl, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <div className="w-10 h-10 mr-3 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg transform rotate-12 shadow-lg"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-lg"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </Link>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/services" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">サービス</Link>
              <Link to="/health-support" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">健康サポート</Link>
              <Link to="/pharmacist-resources" className="text-teal-600 hover:text-teal-700 font-medium whitespace-nowrap cursor-pointer">薬剤師向け</Link>
              <Link to="/information" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">情報提供</Link>
            </nav>
            <button className="md:hidden p-2">
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-indigo-50 py-16">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/e187a8663bdd2e8d0b33d7583853c60d.jpg"
            alt="添付文書検索"
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            <span className="text-blue-600">添付文書</span>検索
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            医薬品の添付文書を素早く検索・閲覧できる総合データベース
          </p>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">薬剤検索</h2>
            
            {/* Search Type Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">検索方法を選択</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {searchCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSearchType(category.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      searchType === category.id
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-blue-50 hover:text-blue-600'
                    }`}
                  >
                    {category.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Search Input */}
            <div className="mb-6">
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={searchCategories.find(cat => cat.id === searchType)?.placeholder}
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                <button
                  onClick={handleSearch}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-blue-600 text-white rounded-lg flex items-center justify-center hover:bg-blue-700 transition-colors cursor-pointer"
                >
                  <i className="ri-search-line"></i>
                </button>
              </div>
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-500">
                検索ボタンをクリックするとPMDA公式サイトが開きます
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">クイックアクセス</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickLinks.map((link, index) => (
              <a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow p-6 border border-gray-100 cursor-pointer"
              >
                <div className={`w-12 h-12 rounded-lg ${link.color} flex items-center justify-center mb-4`}>
                  <i className={`${link.icon} text-xl`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{link.title}</h3>
                <p className="text-gray-600 text-sm">{link.description}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Useful Features */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">便利な機能</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {usefulFeatures.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${feature.icon} text-xl`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 mb-4">{feature.description}</p>
                <ul className="space-y-1">
                  {feature.features.map((item, idx) => (
                    <li key={idx} className="text-sm text-gray-500 flex items-center">
                      <i className="ri-check-line text-blue-600 mr-2"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Updates */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">最新更新情報</h2>
          <div className="max-w-4xl mx-auto">
            <div className="space-y-4">
              {recentUpdates.map((update, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm text-gray-500">{update.date}</span>
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">{update.category}</span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">{update.title}</h3>
                    </div>
                    <i className="ri-arrow-right-line text-gray-400 ml-4"></i>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">検索のコツ</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-blue-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-blue-900 mb-4">効率的な検索方法</h3>
              <ul className="space-y-3 text-blue-800">
                <li className="flex items-start">
                  <i className="ri-lightbulb-line mr-2 mt-1"></i>
                  <span>一般名（成分名）での検索が最も確実</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-lightbulb-line mr-2 mt-1"></i>
                  <span>商品名の一部でも検索可能</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-lightbulb-line mr-2 mt-1"></i>
                  <span>薬効分類コードでの絞り込みが有効</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-lightbulb-line mr-2 mt-1"></i>
                  <span>製薬会社名での検索も可能</span>
                </li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-green-900 mb-4">注意事項</h3>
              <ul className="space-y-3 text-green-800">
                <li className="flex items-start">
                  <i className="ri-alert-line mr-2 mt-1"></i>
                  <span>最新の添付文書であることを必ず確認</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-alert-line mr-2 mt-1"></i>
                  <span>改訂履歴も併せて確認することを推奨</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-alert-line mr-2 mt-1"></i>
                  <span>緊急安全性情報は別途確認が必要</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-alert-line mr-2 mt-1"></i>
                  <span>疑問点は製薬会社に直接問い合わせ</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 mr-2 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg transform rotate-12"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-300 to-emerald-300 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-sm"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-400" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </div>
              <p className="text-gray-400">
                患者さんと薬剤師をつなぐ総合医療情報プラットフォーム
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">検索ツール</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="https://www.pmda.go.jp/PmdaSearch/iyakuSearch/" target="_blank" rel="noopener noreferrer" className="hover:text-white cursor-pointer">PMDA添付文書検索</a></li>
                <li><Link to="/drug-price-search" className="hover:text-white cursor-pointer">薬価サーチ</Link></li>
                <li><a href="https://www.jah.ne.jp/~kako/frame_dwm_search.html" target="_blank" rel="noopener noreferrer" className="hover:text-white cursor-pointer">おくすり110番</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">薬剤師向け</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/pharmacist-resources" className="hover:text-white cursor-pointer">専門リソース</Link></li>
                <li><Link to="/specialty-pharmacy" className="hover:text-white cursor-pointer">専門薬局</Link></li>
                <li><Link to="/sterile-preparation" className="hover:text-white cursor-pointer">無菌調剤</Link></li>
                <li><Link to="/dx" className="hover:text-white cursor-pointer">薬局DX</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">情報・リンク</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/information" className="hover:text-white cursor-pointer">公的機関リンク</Link></li>
                <li><Link to="/guidelines" className="hover:text-white cursor-pointer">疾患ガイドライン</Link></li>
                <li><Link to="/career" className="hover:text-white cursor-pointer">転職情報</Link></li>
                <li><Link to="/entertainment" className="hover:text-white cursor-pointer">エンタメ</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 みんなの薬局～Pharmacare～. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}